import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import styles from './ids-form-layout-group.scss';

import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import {
  FORM_LAYOUT_GROUP_ATTRIBUTES,
  GAP_TYPES,
  IdsFormLayoutGapType,
  PropagatedKeys,
  syncSettingToChild,
  collectPropagationTargets,
  MAX_COLS
} from './ids-form-layout-common';

import type { IdsLabelStateMode } from '../../mixins/ids-label-state-mixin/ids-label-state-common';
import { isLabelStateValid } from '../../mixins/ids-label-state-mixin/ids-label-state-common';

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS Form Layout Group Component
 * @type {IdsFormLayoutGroup}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsLocaleMixin
 * @part group - the form layout group container element
 * @part heading - the optional group heading element
 */
@customElement('ids-form-layout-group')
@scss(styles)
export default class IdsFormLayoutGroup extends Base {
  constructor() {
    super();
  }

  /** Tracks which attributes this group has propagated onto each child */
  #propagatedKeys: PropagatedKeys = new WeakMap();

  /**
   * Re-propagate parent + own settings to children when our own observed
   * attributes change (e.g. parent-inherited gap / label-state / enable-container).
   * @param {string} name attribute name
   * @param {string} oldValue previous value
   * @param {string} newValue new value
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    super.attributeChangedCallback?.(name, oldValue, newValue);
    if (oldValue === newValue) return;
    if ([
      attributes.GAP,
      attributes.LABEL_STATE,
      attributes.ENABLE_CONTAINER
    ].includes(name)) {
      this.#propagateSettingsToChildren();
    }
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array<string>} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      ...FORM_LAYOUT_GROUP_ATTRIBUTES
    ];
  }

  /**
   * Invoked when the component is connected to the DOM
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.#attachSlotChangeListener();
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    const hasLabel = this.label;
    return `
      ${hasLabel ? `<ids-text part="heading" class="ids-form-layout-group-heading" font-size="16" font-weight="semi-bold">${this.label}</ids-text>` : ''}
      <div class="ids-form-layout-group-content" part="group">
        <slot></slot>
      </div>
    `;
  }

  /**
   * Attach slotchange listener to propagate settings when children are slotted
   */
  #attachSlotChangeListener(): void {
    const slot = this.shadowRoot?.querySelector('slot');
    if (slot) {
      this.onEvent('slotchange', slot, () => {
        this.#propagateSettingsToChildren();
      });
    }
  }

  /**
   * Public hook used by a parent form-layout to ask this group to re-read and
   * re-apply inherited settings to its own children.
   */
  propagateSettings(): void {
    this.#propagateSettingsToChildren();
  }

  /**
   * Propagate settings to all child form controls (inherited from parent form-layout)
   */
  #propagateSettingsToChildren(): void {
    // Get parent form-layout settings if available
    const parentFormLayout = this.closest('ids-form-layout');
    if (!parentFormLayout) return;

    // Collect direct propagation targets. Inline wrappers are returned as
    // targets but not descended into — they own their own child controls.
    const children = collectPropagationTargets(this);

    children.forEach((child) => {
      const tagName = child.tagName.toLowerCase();
      if (tagName.startsWith('ids-')) {
        customElements.whenDefined(tagName).then(() => {
          this.#propagateToChild(child as HTMLElement, parentFormLayout as HTMLElement);
        }).catch(() => {
          // Element may not be registered, silently ignore
        });
      }
    });
  }

  /**
   * Propagate settings from parent form-layout to a single child element
   * @param {HTMLElement} child - The child element
   * @param {HTMLElement} parentFormLayout - The parent ids-form-layout element
   */
  #propagateToChild(child: HTMLElement, parentFormLayout: HTMLElement): void {
    const parent = parentFormLayout as any;
    const tracker = this.#propagatedKeys;
    const sync = (attr: string, prop: string, value: unknown, isBoolean = false) => {
      syncSettingToChild(child, attr, prop, value, tracker, isBoolean);
    };

    // Label position / label-related settings (inherited from parent form-layout)
    sync(attributes.LABEL_POSITION, 'labelPosition', parent.labelPosition);
    sync(attributes.LABEL_WIDTH, 'labelWidth', parent.labelWidth);
    sync(attributes.LABEL_WRAP, 'labelWrap', parent.labelWrap);
    sync(attributes.LABEL_ALIGN_Y, 'labelAlignY', parent.labelAlignY);
    sync(attributes.LABEL_ALIGNMENT, 'labelAlignment', parent.labelAlignment);
    // labelState - prefer group's own value, then parent's
    sync(attributes.LABEL_STATE, 'labelState', this.labelState || parent.labelState);

    // Compact and field height
    sync(attributes.COMPACT, 'compact', parent.compact, true);
    sync(attributes.FIELD_HEIGHT, 'fieldHeight', parent.fieldHeight);

    // Field spacing
    sync(attributes.NO_MARGINS, 'noMargins', parent.noMargins, true);
    sync(attributes.MARGIN_BLOCK_END, 'marginBlockEnd', parent.fieldSpacing);

    // Size — mirror the parent form-layout's default: when no size is set, make
    // controls `full` (fluid) so they fill/shrink with the grid track instead of
    // keeping their standalone fixed width and overflowing narrow tracks.
    sync(attributes.SIZE, 'size', parent.size ?? 'full');

    // Validation — skip readonly/disabled controls. Also covers
    // ids-form-layout-inline (which exposes a shared `required` marker).
    const childEditable = !child.hasAttribute(attributes.READONLY) && !child.hasAttribute(attributes.DISABLED);
    sync(attributes.VALIDATE, 'validate', childEditable ? parent.validate : null);
    if (child.tagName.toLowerCase() === 'ids-form-layout-inline') {
      const parentValidate = parent.validate;
      const inlineRequired = typeof parentValidate === 'string' && parentValidate.split(' ').includes('required');
      sync(attributes.REQUIRED, 'required', inlineRequired, true);

      // Inherit gap and container-query behavior down to inline wrappers
      sync(attributes.GAP, 'gap', parent.gap);
      sync(attributes.ENABLE_CONTAINER, 'enableContainer', this.enableContainer || parent.enableContainer, true);

      // Ask the inline wrapper to forward inherited settings to its own children.
      (child as any).propagateSettings?.();
    }
  }

  /**
   * Validate if a value is a valid column count
   * @param {string | number | null} value - The value to validate
   * @returns {boolean} True if valid
   */
  #isValidCols(value: string | number | null): boolean {
    if (value === null || value === undefined || value === '') return false;
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    return !Number.isNaN(num) && num >= 1 && num <= MAX_COLS;
  }

  /**
   * Validate if a value is a valid gap type
   * @param {string | null} value - The value to validate
   * @returns {boolean} True if valid
   */
  #isValidGap(value: string | null): boolean {
    return GAP_TYPES.includes(value as IdsFormLayoutGapType);
  }

  // ================================================
  // Column Settings
  // ================================================

  /**
   * Set the number of columns within the group
   * @param {string | null} value - Number of columns (1-12)
   */
  set cols(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS, String(value));
    } else {
      this.removeAttribute(attributes.COLS);
    }
  }

  /**
   * Get the number of columns within the group
   * @returns {string | null} The number of columns
   */
  get cols(): string | null {
    return this.getAttribute(attributes.COLS);
  }

  /**
   * Set the number of columns at XS breakpoint (320-575px)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsXs(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_XS, String(value));
    } else {
      this.removeAttribute(attributes.COLS_XS);
    }
  }

  /**
   * Get the number of columns at XS breakpoint
   * @returns {string | null} The number of columns
   */
  get colsXs(): string | null {
    return this.getAttribute(attributes.COLS_XS);
  }

  /**
   * Set the number of columns at SM breakpoint (576-767px)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsSm(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_SM, String(value));
    } else {
      this.removeAttribute(attributes.COLS_SM);
    }
  }

  /**
   * Get the number of columns at SM breakpoint
   * @returns {string | null} The number of columns
   */
  get colsSm(): string | null {
    return this.getAttribute(attributes.COLS_SM);
  }

  /**
   * Set the number of columns at MD breakpoint (768-927px)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsMd(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_MD, String(value));
    } else {
      this.removeAttribute(attributes.COLS_MD);
    }
  }

  /**
   * Get the number of columns at MD breakpoint
   * @returns {string | null} The number of columns
   */
  get colsMd(): string | null {
    return this.getAttribute(attributes.COLS_MD);
  }

  /**
   * Set the number of columns at LG breakpoint (928-1183px)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsLg(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_LG, String(value));
    } else {
      this.removeAttribute(attributes.COLS_LG);
    }
  }

  /**
   * Get the number of columns at LG breakpoint
   * @returns {string | null} The number of columns
   */
  get colsLg(): string | null {
    return this.getAttribute(attributes.COLS_LG);
  }

  /**
   * Set the number of columns at XL breakpoint (1184-2463px)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsXl(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_XL, String(value));
    } else {
      this.removeAttribute(attributes.COLS_XL);
    }
  }

  /**
   * Get the number of columns at XL breakpoint
   * @returns {string | null} The number of columns
   */
  get colsXl(): string | null {
    return this.getAttribute(attributes.COLS_XL);
  }

  /**
   * Set the number of columns at XXL breakpoint (2464px+)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsXxl(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_XXL, String(value));
    } else {
      this.removeAttribute(attributes.COLS_XXL);
    }
  }

  /**
   * Get the number of columns at XXL breakpoint
   * @returns {string | null} The number of columns
   */
  get colsXxl(): string | null {
    return this.getAttribute(attributes.COLS_XXL);
  }

  // ================================================
  // Column Span Settings
  // ================================================

  /**
   * Set how many parent columns this group spans
   * @param {string | null} value - Number of columns to span
   */
  set colSpan(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COL_SPAN, String(value));
    } else {
      this.removeAttribute(attributes.COL_SPAN);
    }
  }

  /**
   * Get how many parent columns this group spans
   * @returns {string | null} The number of columns to span
   */
  get colSpan(): string | null {
    return this.getAttribute(attributes.COL_SPAN);
  }

  /**
   * Set col-span at XS breakpoint
   * @param {string | null} value - Number of columns to span
   */
  set colSpanXs(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COL_SPAN_XS, String(value));
    } else {
      this.removeAttribute(attributes.COL_SPAN_XS);
    }
  }

  /**
   * Get col-span at XS breakpoint
   * @returns {string | null} The number of columns to span
   */
  get colSpanXs(): string | null {
    return this.getAttribute(attributes.COL_SPAN_XS);
  }

  /**
   * Set col-span at SM breakpoint
   * @param {string | null} value - Number of columns to span
   */
  set colSpanSm(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COL_SPAN_SM, String(value));
    } else {
      this.removeAttribute(attributes.COL_SPAN_SM);
    }
  }

  /**
   * Get col-span at SM breakpoint
   * @returns {string | null} The number of columns to span
   */
  get colSpanSm(): string | null {
    return this.getAttribute(attributes.COL_SPAN_SM);
  }

  /**
   * Set col-span at MD breakpoint
   * @param {string | null} value - Number of columns to span
   */
  set colSpanMd(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COL_SPAN_MD, String(value));
    } else {
      this.removeAttribute(attributes.COL_SPAN_MD);
    }
  }

  /**
   * Get col-span at MD breakpoint
   * @returns {string | null} The number of columns to span
   */
  get colSpanMd(): string | null {
    return this.getAttribute(attributes.COL_SPAN_MD);
  }

  /**
   * Set col-span at LG breakpoint
   * @param {string | null} value - Number of columns to span
   */
  set colSpanLg(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COL_SPAN_LG, String(value));
    } else {
      this.removeAttribute(attributes.COL_SPAN_LG);
    }
  }

  /**
   * Get col-span at LG breakpoint
   * @returns {string | null} The number of columns to span
   */
  get colSpanLg(): string | null {
    return this.getAttribute(attributes.COL_SPAN_LG);
  }

  /**
   * Set col-span at XL breakpoint
   * @param {string | null} value - Number of columns to span
   */
  set colSpanXl(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COL_SPAN_XL, String(value));
    } else {
      this.removeAttribute(attributes.COL_SPAN_XL);
    }
  }

  /**
   * Get col-span at XL breakpoint
   * @returns {string | null} The number of columns to span
   */
  get colSpanXl(): string | null {
    return this.getAttribute(attributes.COL_SPAN_XL);
  }

  /**
   * Set col-span at XXL breakpoint
   * @param {string | null} value - Number of columns to span
   */
  set colSpanXxl(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COL_SPAN_XXL, String(value));
    } else {
      this.removeAttribute(attributes.COL_SPAN_XXL);
    }
  }

  /**
   * Get col-span at XXL breakpoint
   * @returns {string | null} The number of columns to span
   */
  get colSpanXxl(): string | null {
    return this.getAttribute(attributes.COL_SPAN_XXL);
  }

  // ================================================
  // Gap Settings
  // ================================================

  /**
   * Set the gap between fields within the group
   * @param {IdsFormLayoutGapType} value - Gap size (none, sm, md, lg, xl)
   */
  set gap(value: IdsFormLayoutGapType) {
    if (this.#isValidGap(value as string)) {
      this.setAttribute(attributes.GAP, String(value));
    } else {
      this.removeAttribute(attributes.GAP);
    }
  }

  /**
   * Get the gap between fields within the group
   * @returns {IdsFormLayoutGapType} The gap size
   */
  get gap(): IdsFormLayoutGapType {
    return this.getAttribute(attributes.GAP) as IdsFormLayoutGapType;
  }

  // ================================================
  // Label Settings
  // ================================================

  /**
   * Set the group heading label
   * @param {string | null} value - The heading text
   */
  set label(value: string | null) {
    if (value) {
      this.setAttribute(attributes.LABEL, value);
    } else {
      this.removeAttribute(attributes.LABEL);
    }
    // Re-render to update heading
    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = '';
      this.shadowRoot.innerHTML = this.template();
      this.#attachSlotChangeListener();
    }
  }

  /**
   * Get the group heading label
   * @returns {string | null} The heading text
   */
  get label(): string | null {
    return this.getAttribute(attributes.LABEL);
  }

  // ================================================
  // Label State Settings
  // ================================================

  /**
   * Set the label state for all child form controls within this group
   * @param {IdsLabelStateMode} value - Label state (hidden, collapsed)
   */
  set labelState(value: IdsLabelStateMode) {
    if (isLabelStateValid(value)) {
      if (value) {
        this.setAttribute(attributes.LABEL_STATE, value);
      } else {
        this.removeAttribute(attributes.LABEL_STATE);
      }
      // Re-propagate settings to children when labelState changes
      this.#propagateSettingsToChildren();
    } else {
      this.removeAttribute(attributes.LABEL_STATE);
    }
  }

  /**
   * Get the label state setting
   * @returns {IdsLabelStateMode} The label state
   */
  get labelState(): IdsLabelStateMode {
    return this.getAttribute(attributes.LABEL_STATE) as IdsLabelStateMode;
  }

  // ================================================
  // Container Query Settings
  // ================================================

  /**
   * Set whether to use container queries instead of media queries
   * When enabled, responsive column breakpoints respond to the container size
   * rather than viewport size.
   * @param {boolean | string} value - Whether to enable container queries
   */
  set enableContainer(value: boolean | string) {
    const enabled = stringToBool(value);
    if (enabled) {
      this.setAttribute(attributes.ENABLE_CONTAINER, '');
    } else {
      this.removeAttribute(attributes.ENABLE_CONTAINER);
    }
  }

  /**
   * Get the enable-container setting
   * @returns {boolean} Whether container queries are enabled
   */
  get enableContainer(): boolean {
    return stringToBool(this.getAttribute(attributes.ENABLE_CONTAINER));
  }
}
